import { defineComponent } from "vue";
import moment from "moment";
import { adjustDate } from "../../../../common/adjust-date";

export default defineComponent({
    props: {
        startDate: { default: null },
        endDate: { default: null },
        disabled: { type: Boolean, default: false }
    },

    emits: ["taskRefresh"],

    data() {
        return {
            todaysDate: new Date(Date.now()) as Date,
            showCalendar: false,
            selectedDate: null as Date | null
        };
    },

    computed: {
        formattedDateRange(): string {
            if (!this.startDate || !this.endDate) return "";
            const fmt = "MMM D, YYYY";
            return `${moment(this.startDate).format(fmt)} - ${moment(this.endDate).format(fmt)}`;
        },
        minAllowedDate(): Date {
            return moment(this.todaysDate).subtract(6, "months").startOf("day").toDate();
        },
        maxAllowedDate(): Date {
            return moment(this.todaysDate).add(6, "months").endOf("day").toDate();
        },
        isPrevDisabled(): boolean {
            if (!this.startDate) return false;
            return moment(this.startDate).subtract(1, "day").isBefore(this.minAllowedDate, "day");
        },
        isNextDisabled(): boolean {
            if (!this.startDate) return false;
            return moment(this.endDate).add(1, "day").isAfter(this.maxAllowedDate, "day");
        }
    },

    watch: {
        startDate() {
            this.$nextTick(() => {
                this.initWeekPicker();
                this.refreshPicker();
            });
        }
    },

    async mounted() {
        this.initWeekPicker();
        await this.initDateRange();
        document.addEventListener("click", this.onOutsideClick);
    },

    beforeUnmount() {
        document.removeEventListener("click", this.onOutsideClick);
    },

    methods: {
        toggleCalendar(): void {
            this.showCalendar = !this.showCalendar;
            if (this.showCalendar) {
                this.$nextTick(() => {
                    this.initWeekPicker();
                    this.refreshPicker();
                });
            }
        },

        onOutsideClick(e: Event): void {
            const root = this.$refs.weeklyNav as HTMLElement;
            if (root && !root.contains(e.target as Node)) {
                this.showCalendar = false;
            }
        },

        isInSelectedWeek(date: Date): boolean {
            if (!this.startDate || !this.endDate) return false;
            return moment(date).isBetween(
                moment(this.startDate).startOf("day"),
                moment(this.endDate).endOf("day"),
                undefined,
                "[]"
            );
        },

        initWeekPicker(): void {
            const el = this.$refs.weekDatePicker as HTMLElement;
            const weekStart = moment(this.startDate ?? this.todaysDate).day();
            $(el).datepicker("destroy");
            $(el)
                .datepicker({
                    keyboardNavigation: false,
                    maxViewMode: 0,
                    weekStart,
                    startDate: this.minAllowedDate,
                    endDate: this.maxAllowedDate,
                    beforeShowDay: (date: Date) => (this.isInSelectedWeek(date) ? "week-selected" : "")
                })
                .datepicker("update", this.startDate ?? this.todaysDate)
                .off("changeDate")
                .on("changeDate", (e: DatepickerEventObject) => {
                    this.changeDate(e.date);
                    e.preventDefault();
                    e.stopPropagation();
                });
        },

        refreshPicker(): void {
            const el = this.$refs.weekDatePicker as HTMLElement;
            if (!el || !this.startDate) return;
            if (!this.selectedDate || !this.isInSelectedWeek(this.selectedDate)) {
                this.selectedDate = this.isInSelectedWeek(this.todaysDate) ? this.todaysDate : this.startDate;
            }
            $(el).datepicker("update", this.selectedDate);
        },

        async changeDate(selected: Date) {
            this.selectedDate = selected;
            const weekStartDay = moment(this.startDate ?? this.todaysDate).day();
            const diff = (moment(selected).day() - weekStartDay + 7) % 7;
            const start = moment(selected).subtract(diff, "days").toDate();
            const end = moment(start).add(6, "days").toDate();

            this.showCalendar = false;
            await this.emitTaskRefresh(start, end);
        },

        async initDateRange() {
            if (!!this.startDate && !!this.endDate) {
                await this.emitTaskRefresh(this.startDate, this.endDate);
            }
        },

        async forwardNavigation() {
            if (this.isNextDisabled) return;
            await this.dateNavigation(7);
        },

        async backwardNavigation() {
            if (this.isPrevDisabled) return;
            await this.dateNavigation(-7);
        },

        async dateNavigation(offset: number) {
            await this.emitTaskRefresh(adjustDate(this.startDate, offset), adjustDate(this.endDate, offset));
        },

        async emitTaskRefresh(startDate: Date, endDate: Date) {
            this.$emit("taskRefresh", { startDate, endDate });
        }
    }
});
